import { MedplumClient, QueryTypes, ResourceArray } from "@medplum/core";
import { Bundle, BundleLink, Resource } from "@medplum/fhirtypes";

import { getResourcesFromBundle } from "@/utils/medplum";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

/**
 * Result type for paginated resource fetching operations.
 * Discriminated union that distinguishes between successful fetches and errors,
 * preventing confusion between "no results" and "fetch failed" scenarios.
 */
export type PaginatedResult<T> =
  | { success: true; data: T }
  | { success: false; error: Error; data: T };

/**
 * Result type for paginated resources with revinclude.
 */
export type PaginatedRevincludeResult<TPrimary, TIncluded> = PaginatedResult<{
  primary: TPrimary[];
  included: TIncluded[];
}>;

/**
 * Parses URL parameters from a Bundle.link URL
 * @param url - The URL from Bundle.link
 * @returns Object with search parameters
 */
export function parseSearchParams(url: string): Record<string, string> {
  try {
    const urlObj = new URL(url);
    const params: Record<string, string> = {};

    // Extract all search parameters
    for (const [key, value] of urlObj.searchParams.entries()) {
      params[key] = value;
    }

    return params;
  } catch (error) {
    captureExceptionWithoutPhi("Failed to parse pagination link URL", error);
    return {};
  }
}

/**
 * Parses Medplum RateLimit header to extract retry delay
 *
 * Medplum has two types of rate limits:
 * 1. "requests" - HTTP request count limit per IP (6,000-60,000 per minute depending on tier)
 * 2. "fhirInteractions" - Weighted FHIR operation quota (operations weighted by complexity:
 *    Read=1pt, Search=20pts, Create/Update/Delete/Patch=100pts, History=10pts)
 *
 * Format: "limitType";r=<remaining>;t=<seconds>
 * Multiple limits can be comma-separated: "requests";r=59083;t=39, "fhirInteractions";r=0;t=19
 *
 * When a 429 error occurs, we prioritize exhausted limits (r=0) and wait for the longest
 * reset time among exhausted limits. This ensures we wait for the limit that caused the 429.
 *
 * @param rateLimitHeader - The RateLimit header value (may be undefined)
 * @returns The reset time in seconds for exhausted limits, or max of all limits if none exhausted, or null if invalid
 */
export function parseRateLimitHeader(rateLimitHeader: string | undefined): number | null {
  if (!rateLimitHeader) {
    return null;
  }

  try {
    // Split by comma to handle multiple limits (format: "requests";r=59999;t=60, "fhirInteractions";r=49894;t=60)
    const limits = rateLimitHeader.split(",").map((limit) => limit.trim());
    const exhaustedTimeValues: number[] = [];
    const allTimeValues: number[] = [];

    for (const limit of limits) {
      // Extract r=<remaining> and t=<seconds> from format: "limitType";r=<num>;t=<seconds>
      const rMatch = limit.match(/;r=(\d+)/);
      const tMatch = limit.match(/;t=(\d+)/);

      if (tMatch && tMatch[1]) {
        const timeValue = parseInt(tMatch[1], 10);
        if (!isNaN(timeValue) && timeValue >= 0) {
          allTimeValues.push(timeValue);

          // If this limit is exhausted (r=0), prioritize its reset time
          if (rMatch && rMatch[1] && parseInt(rMatch[1], 10) === 0) {
            exhaustedTimeValues.push(timeValue);
          }
        }
      }
    }

    // Prioritize exhausted limits - if any are exhausted, use the max of those
    // Otherwise, use the max of all limits (safety fallback)
    if (exhaustedTimeValues.length > 0) {
      return Math.max(...exhaustedTimeValues);
    }
    return allTimeValues.length > 0 ? Math.max(...allTimeValues) : null;
  } catch (error) {
    captureExceptionWithoutPhi("Failed to parse RateLimit header", error);
    return null;
  }
}

interface PaginationOptions {
  delayBetweenPages?: number; // default 50ms
  maxRetries?: number; // default 3
  /**
   * Whether a failed or repeated `next` page throws instead of returning the pages read so far.
   * Off by default, because most callers render a list and a short list beats an error. Turn it on
   * when a partial read would be taken as data that is not there, such as a version history a diff
   * compares against.
   */
  throwOnPageError?: boolean;
  /**
   * Awaited before each `next`-page request. Lets a caller pace pagination against the server's
   * remaining quota (e.g. `waitForRateLimitRenewalIfNeeded`) instead of relying on the fixed
   * inter-page delay and 429 retries alone.
   */
  beforePageFetch?: () => Promise<void>;
  /**
   * Passed to every `next`-page request. An aborted request rethrows its `AbortError`, so a caller
   * torn down mid-fetch can tell a cancelled read from a partial one.
   */
  signal?: AbortSignal;
}

/**
 * Normalizes QueryTypes to Record format and ensures _count is set
 * @param searchParams - Search parameters to normalize
 * @param defaultCount - Default count value (defaults to 500)
 * @returns Normalized search parameters with _count set
 */
function normalizeSearchParams(
  searchParams: QueryTypes,
  defaultCount = 500,
): Record<string, string | number | boolean | undefined> {
  const paramsRecord =
    typeof searchParams === "object" && !Array.isArray(searchParams) && searchParams !== null
      ? (searchParams as Record<string, string | number | boolean | undefined>)
      : {};
  return {
    ...paramsRecord,
    _count: paramsRecord._count ?? defaultCount,
  };
}

/**
 * Waits before the next page or retry, ending early when the caller aborts.
 * @param ms - How long to wait
 * @param signal - Aborting it rejects the wait with an `AbortError`, the same error an aborted
 *   request rejects with
 * @returns A promise that resolves after `ms`
 */
function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const abortError = () =>
      Object.assign(new Error("Pagination was aborted"), { name: "AbortError" });
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Fetches a bundle with retry logic for rate limiting
 * @param medplum - Medplum client instance
 * @param url - URL to fetch
 * @param maxRetries - Maximum number of retries
 * @param signal - Passed to each request; aborting it also ends a rate-limit wait
 * @returns The fetched bundle
 */
async function fetchBundleWithRetry(
  medplum: MedplumClient,
  url: string,
  maxRetries: number,
  signal?: AbortSignal,
): Promise<Bundle> {
  let retryCount = 0;

  while (retryCount <= maxRetries) {
    try {
      return (await (signal ? medplum.get(url, { signal }) : medplum.get(url))) as Bundle;
    } catch (error: unknown) {
      const httpError = error as { status?: number; headers?: Record<string, string> };
      if (httpError.status === 429 && retryCount < maxRetries) {
        // Rate limited - parse Medplum RateLimit header or fallback to exponential backoff
        // Check for RateLimit header (case-insensitive using regex)
        const headers = httpError.headers || {};
        const rateLimitHeaderKey = Object.keys(headers).find((key) => /^ratelimit$/i.test(key));
        const rateLimitHeader = rateLimitHeaderKey ? headers[rateLimitHeaderKey] : undefined;
        const retryDelaySeconds = parseRateLimitHeader(rateLimitHeader) ?? Math.pow(2, retryCount);
        const delayMs = retryDelaySeconds * 1000;
        await delay(delayMs, signal);
        retryCount++;
      } else {
        throw error;
      }
    }
  }

  throw new Error(`Failed to fetch bundle after ${maxRetries} retries`);
}

/**
 * Collects resources from an initial Bundle and any `next` pages (e.g. `readHistory` pagination).
 *
 * Use this when you already have a Bundle (such as `medplum.readHistory`). For search-based
 * pagination, prefer {@link fetchAllPaginatedResources}, which performs the initial search and
 * follows `next` links.
 */
export async function collectResourcesFromPaginatedBundle<T extends Resource>(
  medplum: MedplumClient,
  initialBundle: Bundle | undefined,
  resourceType: T["resourceType"],
  options?: PaginationOptions,
): Promise<T[]> {
  const resources: T[] = [];

  const appendFromBundle = (bundle: Bundle | undefined) => {
    if (!bundle?.entry) {
      return;
    }
    resources.push(...getResourcesFromBundle<T>(resourceType, bundle as Bundle<T>));
  };

  appendFromBundle(initialBundle);
  await processPaginatedBundles(
    medplum,
    initialBundle,
    options ?? {},
    appendFromBundle,
    resourceType,
  );

  return resources;
}

/**
 * Collects resources from the `next` pages that follow a Bundle, leaving that Bundle's own entries
 * out.
 *
 * For callers that already hold the first page's resources, such as `searchResources`, which returns
 * them unwrapped alongside the bundle. Use {@link collectResourcesFromPaginatedBundle} when the
 * bundle itself is the only thing in hand.
 */
export async function collectResourcesFromNextPages<T extends Resource>(
  medplum: MedplumClient,
  initialBundle: Bundle | undefined,
  resourceType: T["resourceType"],
  options?: PaginationOptions,
): Promise<T[]> {
  const resources: T[] = [];

  await processPaginatedBundles(
    medplum,
    initialBundle,
    options ?? {},
    (bundle) => {
      if (!bundle.entry) {
        return;
      }
      resources.push(...getResourcesFromBundle<T>(resourceType, bundle as Bundle<T>));
    },
    resourceType,
  );

  return resources;
}

/**
 * Processes paginated bundles with a callback function.
 */
async function processPaginatedBundles(
  medplum: MedplumClient,
  initialBundle: Bundle | undefined,
  options: PaginationOptions,
  processBundle: (bundle: Bundle) => void,
  resourceType: string,
): Promise<void> {
  const {
    delayBetweenPages = 50,
    maxRetries = 3,
    beforePageFetch,
    throwOnPageError = false,
    signal,
  } = options;
  let nextLink = initialBundle?.link?.find((link: BundleLink) => link.relation === "next");
  // A server that hands back the same `next` URL again would otherwise loop forever.
  const requestedUrls = new Set<string>();

  while (nextLink?.url) {
    if (requestedUrls.has(nextLink.url)) {
      // The pages after it were never read, so the result is as incomplete as a failed page.
      // The URL carries search params, so only the resource type goes in the message.
      if (throwOnPageError) {
        throw new Error(`${resourceType} pagination returned a repeated next URL`);
      }
      console.warn(`Stopping ${resourceType} pagination at a repeated next URL.`);
      break;
    }
    requestedUrls.add(nextLink.url);

    try {
      // Add delay between pages to avoid rate limit bursts
      if (delayBetweenPages > 0) {
        await delay(delayBetweenPages, signal);
      }

      // Every page read is its own search request, so it must be paced like the initial one.
      if (beforePageFetch) {
        await beforePageFetch();
      }

      const nextBundle = await fetchBundleWithRetry(medplum, nextLink.url, maxRetries, signal);
      processBundle(nextBundle);

      // Find next page link
      nextLink = nextBundle?.link?.find((link: BundleLink) => link.relation === "next");
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") throw error;
      captureExceptionWithoutPhi("Failed to fetch a paginated resource page", error, {
        resourceType,
      });
      if (throwOnPageError) {
        throw error;
      }
      // The URL carries search params and the error can quote them, so only the resource type is
      // logged; the error itself went to Sentry above.
      console.warn(`Stopping ${resourceType} pagination at a page that failed to load.`);
      break;
    }
  }
}

/**
 * Searches one page at a time, for callers that must act on each page before reading the next.
 * Every page read, the first included, waits out a 429 like {@link fetchAllPaginatedResources}.
 * A failed page read and a repeated `next` URL both throw, so the caller never takes a partial
 * read for the whole result.
 * @param medplum - Medplum client instance
 * @param resourceType - FHIR resource type to search
 * @param searchParams - Search parameters (will use _count: 500 if not specified)
 * @param options - Pagination options; `throwOnPageError` is always on
 * @yields The resources of one page, in server order
 */
export async function* searchPaginatedResourcePages<T extends Resource>(
  medplum: MedplumClient,
  resourceType: T["resourceType"],
  searchParams: QueryTypes,
  options?: Omit<PaginationOptions, "throwOnPageError">,
): AsyncGenerator<T[]> {
  const { delayBetweenPages = 50, maxRetries = 3, beforePageFetch, signal } = options ?? {};
  const searchUrl = medplum.fhirSearchUrl(resourceType, normalizeSearchParams(searchParams));
  let url: string | undefined = searchUrl.toString();
  const requestedUrls = new Set<string>();

  while (url) {
    if (requestedUrls.has(url)) {
      // The URL carries search params, so only the resource type goes in the message.
      throw new Error(`${resourceType} pagination returned a repeated next URL`);
    }
    if (requestedUrls.size > 0 && delayBetweenPages > 0) {
      await delay(delayBetweenPages, signal);
    }
    requestedUrls.add(url);
    if (beforePageFetch) {
      await beforePageFetch();
    }
    const bundle = await fetchBundleWithRetry(medplum, url, maxRetries, signal);
    const resources = getResourcesFromBundle<T>(resourceType, bundle as Bundle<T>);
    if (resources.length > 0) {
      yield resources;
    }
    url = bundle.link?.find((link: BundleLink) => link.relation === "next")?.url;
  }
}

/**
 * Fetches all paginated resources by following Bundle.link navigation
 * Handles rate limiting with delays and 429 error retries
 * @param medplum - Medplum client instance
 * @param resourceType - FHIR resource type to fetch
 * @param searchParams - Search parameters (will use _count: 500 if not specified)
 * @param options - Pagination options
 * @returns PaginatedResult with success status and data. On error, returns { success: false, error, data: [] }
 */
export async function fetchAllPaginatedResources<T extends Resource>(
  medplum: MedplumClient,
  resourceType: T["resourceType"],
  searchParams: QueryTypes,
  options?: PaginationOptions,
): Promise<PaginatedResult<T[]>> {
  const allResources: T[] = [];

  try {
    const paramsWithCount = normalizeSearchParams(searchParams);

    // Initial search - searchResources returns ResourceArray which extends T[] with a .bundle property
    // We use type assertion because the generic T extends Resource doesn't satisfy the exact
    // WithId<ExtractResource<RT>> return type, but the runtime types are compatible
    const initialResult = await medplum.searchResources(resourceType, paramsWithCount, {
      cache: "no-cache",
    });
    const initialBundle = (initialResult as ResourceArray<T>).bundle;

    // Extract resources from array directly (searchResources unwraps the bundle)
    allResources.push(...(initialResult as unknown as T[]));

    // Process remaining pages
    await processPaginatedBundles(
      medplum,
      initialBundle,
      options || {},
      (bundle) => {
        if (bundle?.entry) {
          const resources = getResourcesFromBundle<T>(resourceType, bundle as Bundle<T>);
          allResources.push(...(resources as T[]));
        }
      },
      resourceType,
    );

    return { success: true, data: allResources };
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    captureExceptionWithoutPhi("Failed to fetch paginated resources", err, { resourceType });
    return { success: false, error: err, data: [] };
  }
}

/**
 * Fetches all paginated resources with _revinclude, returning both primary and included resources.
 *
 * This function extracts resources from the bundle entries (not the array returned by searchResources)
 * because _revinclude adds related resources to the bundle that aren't in the unwrapped array.
 *
 * @param medplum - Medplum client instance
 * @param primaryResourceType - Primary FHIR resource type
 * @param includedResourceType - Included FHIR resource type from _revinclude
 * @param searchParams - Search parameters (should include _revinclude)
 * @param options - Pagination options
 * @returns PaginatedRevincludeResult with success status and data. On error, returns { success: false, error, data: { primary: [], included: [] } }
 */
export async function fetchAllPaginatedResourcesWithRevinclude<
  TPrimary extends Resource,
  TIncluded extends Resource,
>(
  medplum: MedplumClient,
  primaryResourceType: TPrimary["resourceType"],
  includedResourceType: TIncluded["resourceType"],
  searchParams: QueryTypes,
  options?: PaginationOptions,
): Promise<PaginatedRevincludeResult<TPrimary, TIncluded>> {
  const allPrimaryResources: TPrimary[] = [];
  const allIncludedResources: TIncluded[] = [];

  try {
    const paramsWithCount = normalizeSearchParams(searchParams);

    // Initial search - searchResources returns ResourceArray which extends T[] with a .bundle property
    // Per Medplum SDK types, .bundle is always present on ResourceArray (not optional)
    // We use type assertion because the generic TPrimary extends Resource doesn't satisfy the exact
    // WithId<ExtractResource<RT>> return type, but the runtime types are compatible
    const initialResult = await medplum.searchResources(primaryResourceType, paramsWithCount);
    const initialBundle = (initialResult as ResourceArray<TPrimary>).bundle;

    // For _revinclude searches, we must extract from bundle.entry to get both primary and included resources.
    // The unwrapped array only contains primary resources; included resources are only in the bundle.
    if (initialBundle.entry) {
      // Extract primary resources from bundle entries
      const primaryResources = getResourcesFromBundle<TPrimary>(
        primaryResourceType,
        initialBundle as Bundle<TPrimary>,
      );
      allPrimaryResources.push(...(primaryResources as TPrimary[]));

      // Extract included resources from bundle entries (cast through unknown for different resource types)
      const includedResources = getResourcesFromBundle<TIncluded>(
        includedResourceType,
        initialBundle as unknown as Bundle<TIncluded>,
      );
      allIncludedResources.push(...(includedResources as TIncluded[]));
    }
    // Note: If bundle.entry is empty/undefined, both arrays remain empty (legitimate empty result)

    // Process remaining pages
    await processPaginatedBundles(
      medplum,
      initialBundle,
      options || {},
      (bundle) => {
        if (bundle?.entry) {
          // Extract primary resources
          const primaryResources = getResourcesFromBundle<TPrimary>(
            primaryResourceType,
            bundle as Bundle<TPrimary>,
          );
          allPrimaryResources.push(...(primaryResources as TPrimary[]));

          // Extract included resources
          const includedResources = getResourcesFromBundle<TIncluded>(
            includedResourceType,
            bundle as Bundle<TIncluded>,
          );
          allIncludedResources.push(...(includedResources as TIncluded[]));
        }
      },
      primaryResourceType,
    );

    return {
      success: true,
      data: {
        primary: allPrimaryResources,
        included: allIncludedResources,
      },
    };
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    captureExceptionWithoutPhi("Failed to fetch paginated resources with revinclude", err, {
      resourceType: primaryResourceType,
    });
    return {
      success: false,
      error: err,
      data: {
        primary: [],
        included: [],
      },
    };
  }
}
